import { extractDefaultPreferences } from './utils';

import {
    INIT_SESSION,
    DESTROY_SESSION,
    SELECT_PROPERTY,
    RESET_APP,
    CHANGE_LOCALE,
    SET_PREFERENCES,
    SET_CONFIG,
    SET_AUTHORISED_PROPERTIES,
    SET_PROPERTY_EXTRA_INFO,
} from './constants';

/**
 * @typedef PropertyConfig
 * @type {Object}
 * @property {Object} sellingRate
 * @property {String} sellingRate.defaultRoom - uuid
 */

/**
 * @typedef PropertyObject
 * @type {Object}
 * @property {String} id - uuid
 * @property {String} name
 * @property {Number} roomCount - number of physical rooms in property
 * @property {Number} vendorID
 * @property {String} financialYearStart - 4 characters: month + day of month
 *                                         i.e. "0401" (1st of April)
 * @property {String} currency
 * @property {String[]} features - features that are accessible for this property to the user
 * @property {Boolean} isTwoWayEnabled - true if the property has two way enabled
 * @property {PropertyConfig} config
 */

/**
 * Creates an action to initialise the session object
 * @param  {Object} userInfo
 * @param  {Number} userInfo.userId
 * @param  {String} userInfo.uuid
 * @param  {String} userInfo.firstName
 * @param  {String} userInfo.lastName
 * @param  {String} userInfo.email
 * @param  {String} userInfo.locale - BCP 47 language/locale tag
 * @param  {Object} preferences - an object with all the user preferences
 * @param  {String} token - JWT
 * @param  {Object.<String, PropertyObject>} authorisedProperties - a list of properties
 *                    the user is allowed to see. Keys = propertyId
 * @param  {String} [selectedProperty=null] [description]
 * @return {Object} the action
 */
export const initSession = (userInfo, preferences, token, authorisedProperties, selectedProperty = null) => ({
    type: INIT_SESSION,
    session: {
        userInfo,
        preferences,
        token,
        authorisedProperties,
        selectedProperty,
    },
});

/**
 * Creates an action to destroy the session (i.e. logout)
 * @return {Object} the action
 */
export const destroySession = () => ({
    type: DESTROY_SESSION,
});

/**
 * Creates an action to set the selected property
 * @param  {String} propertyId - uuid of selected property
 * @return {Object} the action
 */
export const selectProperty = (propertyId) => ({
    type: SELECT_PROPERTY,
    propertyId,
});

/**
 * Creates an action to reset the app to its initial state
 * (Will trigger session destruction)
 * @return {Object} the action
 */
export const resetApp = () => ({
    type: RESET_APP,
});

/**
 * Creates an action to change the locale
 * @param  {String} locale - BCP 47 language/locale tag
 * @return {Object} the action
 */
export const changeLocale = (locale) => ({
    type: CHANGE_LOCALE,
    locale,
});

/**
 * Creates an action to update a single user preference given its key & new value
 * @param  {String} key
 * @param  {*}      value
 * @return {Object} the action
 */
export const updatePreference = (key, value) => ({
    type: SET_PREFERENCES,
    preferences: { [key]: value },
});

/**
 * Creates an action to update a list of preferences
 * @param  {Object} preferences - the preferences object to override with
 * @return {Object} the action
 */
export const updatePreferences = (preferences) => ({
    type: SET_PREFERENCES,
    preferences,
});

/**
 * Creates an action to reset the given preferences to their default value
 * @param  {String[]} preferencesKeys - list of preferences' keys
 * @return {Object}   the action
 */
export const resetPreferences = (preferencesKeys) => ({
    type: SET_PREFERENCES,
    preferences: extractDefaultPreferences(preferencesKeys),
});

/**
 * Creates an action to update a single user config item given its key & new value
 * @param  {String} key
 * @param  {*}      value
 * @return {Object} the action
 */
export const updateConfigItem = (key, value) => ({
    type: SET_CONFIG,
    config: { [key]: value },
});

/**
 * Creates an action to update the entire user config object
 * @param  {Object} config - the config object to override with
 * @return {Object} the action
 */
export const updateConfig = (config) => ({
    type: SET_CONFIG,
    config,
});

/**
 * Creates an action to update the entire authorised properties object
 * @param  {Object} authorisedProperties - the authorisedProperties object to override with
 * @return {Object} the action
 */
export const updateAuthorisedProperties = (authorisedProperties) => ({
    type: SET_AUTHORISED_PROPERTIES,
    authorisedProperties,
});

/**
 * Creates an action to update the entire authorised properties object
 * @param  {Object} authorisedProperties - the authorisedProperties object to override with
 * @return {Object} the action
 */
export const updateSelectedPropertyExtraDetails = (authorisedProperties) => ({
    type: SET_PROPERTY_EXTRA_INFO,
    authorisedProperties,
});
